<template>
  <div class="video-palyer" v-if="reseted">
    <video ref="video" class="video-js">
      <source
        v-for="item in sources"
        :key="item.src"
        :src="item.src"
        :type="item.type"
      />
    </video>
    <!-- <button @click="$refs.video.play()">Force Play</button> -->
    <slot />
  </div>
</template>

<script>
import videojs from "video.js";

const Player = videojs.getComponent("Player");

Player.prototype.play = function play() {
  let retval = this.techGet_("play");
  // silence errors (unhandled promise from play)
  if (retval !== undefined && typeof retval.then === "function") {
    retval.then(null, () => {});
  }
  return retval;
};

const DEFAULT_EVENTS = [
  "loadeddata",
  "canplay",
  "canplaythrough",
  "play",
  "pause",
  "waiting",
  "playing",
  "ended",
  "error"
];

export default {
  name: "VideoPlayer",
  props: {
    playsinline: Boolean,
    start: {
      type: Number,
      default: 0
    },
    crossOrigin: {
      type: String,
      default: ""
    },
    customEventName: {
      type: String,
      default: "statechanged"
    },
    options: {
      type: Object,
      default: () => ({})
    },
    events: {
      type: Array,
      default: () => []
    },
    globalOptions: {
      type: Object,
      default: () => ({
        controls: true,
        controlBar: {
          fullscreenToggle: false,
          pictureInPictureToggle: false
        },
        techOrder: ["html5"],
        plugins: {}
      })
    },
    globalEvents: {
      type: Array,
      default: () => []
    },
    trackList: {
      type: Array,
      default: () => []
    },
    sources: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      player: null,
      reseted: true
    };
  },

  watch: {
    options: {
      deep: true,
      handler(options) {
        this.dispose(() => {
          if (options && options.sources && options.sources.length) {
            this.initialize();
          }
        });
      }
    }
  },

  mounted() {
    if (!this.player) {
      this.initialize();
    }
  },

  beforeDestroy() {
    setTimeout(() => {
      if (this.player) {
        this.dispose();
      }
    }, 500);
  },

  methods: {
    initialize() {
      const videoOptions = Object.assign({}, this.globalOptions, this.options);

      // ios fullscreen
      if (this.playsinline) {
        this.$refs.video.setAttribute("playsinline", this.playsinline);
        this.$refs.video.setAttribute("webkit-playsinline", this.playsinline);
        this.$refs.video.setAttribute("x5-playsinline", this.playsinline);
        this.$refs.video.setAttribute("x5-video-player-type", "h5");
        this.$refs.video.setAttribute("x5-video-player-fullscreen", false);
      }

      // cross origin
      if (this.crossOrigin !== "") {
        this.$refs.video.crossOrigin = this.crossOrigin;
        this.$refs.video.setAttribute("crossOrigin", this.crossOrigin);
      }

      // emit event
      const emitPlayerState = (event, value) => {
        if (event) {
          this.$emit(event, this.player);
        }
        if (value) {
          this.$emit(this.customEventName, { [event]: value });
        }
      };

      // avoid error "VIDEOJS: ERROR: Unable to find plugin: __ob__"
      if (videoOptions.plugins) {
        delete videoOptions.plugins.__ob__;
      }

      // player
      const self = this;
      this.player = videojs(this.$refs.video, videoOptions, function() {
        this.addClass("vjs-waiting");

        // events
        const events = DEFAULT_EVENTS.concat(self.events).concat(
          self.globalEvents
        );
        // watch events
        const onEdEvents = {};
        for (let i = 0; i < events.length; i++) {
          if (
            typeof events[i] === "string" &&
            onEdEvents[events[i]] === undefined
          ) {
            (event => {
              onEdEvents[event] = null;
              this.on(event, () => {
                emitPlayerState(event, true);
              });
            })(events[i]);
          }
        }
        // watch timeupdate
        this.on("timeupdate", function() {
          emitPlayerState("timeupdate", this.currentTime());
        });
        // player readied
        self.$emit("ready", this);
      });
    },

    dispose(callback) {
      if (this.player && this.player.dispose) {
        if (this.player.techName_ !== "Flash") {
          this.player.pause && this.player.pause();
        }
        this.player.dispose();
        this.player = null;

        this.$nextTick(() => {
          this.reseted = false;
          this.$nextTick(() => {
            this.reseted = true;
            this.$nextTick(() => {
              callback && callback();
            });
          });
        });
      }
    }
  }
};
</script>

<style lang="scss">
/* .video-palyer {} */

.video-js {
  overflow: hidden;

  video {
    pointer-events: none;
  }

  .vjs-control-bar {
    background-color: rgba(#000, 0.7);
    transition: $primary-transition;
    transform: translate(0, 0);
    opacity: 1;
    transition-property: transform, opacity;
    will-change: transform;
    display: flex;

    &.vjs-hidden {
      display: flex !important;
      transform: translate(0, 100%);
      opacity: 0;
    }
  }

  .vjs-control.vjs-record-indicator {
    line-height: 1;
    opacity: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0 10px;
    margin-left: auto;

    .vjs-record-indicator__wrap {
      display: flex;
      min-width: 0;
      align-items: center;
      justify-content: center;

      span {
        color: white;
        font-family: "Helvetica", sans-serif;
        animation-name: record-pulsetext;
        animation-duration: 5s;
        animation-iteration-count: infinite;
        line-height: 1em;
        font-size: 1em;
        opacity: 1;
        transition: opacity 0.5s 0.2s;
        margin-right: 6px;
      }

      i {
        width: 0.8em;
        height: 0.8em;
        background-color: #ff625e;
        line-height: 1em;
        border-radius: 50%;
        // right: 2.5ex;
        // top: 2ex;
        position: relative;
        opacity: 1;
        transform: rotateX(0) scale(1.3);
        transition: all 0.5s, transform 0.2s 0.1s;

        &:after {
          content: "";
          display: block;
          height: calc(0.8em - 4px);
          width: calc(0.8em - 4px);
          border: 2px solid #ff625e;
          border-radius: 50%;
          position: absolute;
          top: 2px;
          left: 2px;
          transform-origin: center;
          animation-name: record-pulse;
          animation-duration: 2s;
          animation-iteration-count: infinite;
          animation-delay: 0.25s;
        }
      }
    }
  }

  .vjs-disabled {
    cursor: default !important;
  }
}

@keyframes record-pulsetext {
  from {
    opacity: 0.75;
  }
  25% {
    opacity: 0.5;
  }
  50% {
    opacity: 0.75;
  }
  75% {
    opacity: 0.5;
  }
  to {
    opacity: 0.75;
  }
}

@keyframes record-pulse {
  from {
    opacity: 1;
    transform: scale(1.6);
  }
  50% {
    opacity: 0.5;
    transform: scale(2.5);
  }
  to {
    opacity: 0;
    transform: scale(3.5);
  }
}
</style>
